import * as z from "zod";

/**
 * Resolve (deflection) analytics shapes, mirroring `dispute-alert-analytics.ts`.
 * `*ByCurrency` rows are the repo SELECT (matched value in integer MINOR units).
 */

// Bucket key is COALESCE(transaction_currency, 'USD'): matched value is the only
// money metric; counts and SLA/latency are currency-agnostic and sum across.
export const deflectionStatsByCurrencySchema = z.object({
  currency: z.string(),
  totalCount: z.coerce.number(),
  matchCount: z.coerce.number(),
  noMatchCount: z.coerce.number(),
  multipleCount: z.coerce.number(),
  timeoutCount: z.coerce.number(),
  schemaErrorCount: z.coerce.number(),
  partnerErrorCount: z.coerce.number(),
  respondedCount: z.coerce.number(),
  slaMetCount: z.coerce.number(),
  matchedValueAmountMinor: z.coerce.number(),
});

export interface DeflectionStatsByCurrency extends z.infer<
  typeof deflectionStatsByCurrencySchema
> {}

/** Single account-wide latency row; null when no responses in range. */
export const deflectionLatencyPercentilesSchema = z.object({
  p50LatencyMs: z.coerce.number().nullable(),
  p95LatencyMs: z.coerce.number().nullable(),
});

export type DeflectionLatencyPercentiles = z.infer<
  typeof deflectionLatencyPercentilesSchema
>;

/** Per (month, currency) inquiry volume + matched value on `received_at`. */
export const monthlyDeflectionStatsByCurrencySchema = z.object({
  month: z.coerce.date(),
  currency: z.string(),
  totalCount: z.coerce.number(),
  matchCount: z.coerce.number(),
  matchedValueAmountMinor: z.coerce.number(),
});

export interface MonthlyDeflectionStatsByCurrency extends z.infer<
  typeof monthlyDeflectionStatsByCurrencySchema
> {}

/** USD-merged monthly row returned to the client. */
export const monthlyDeflectionStatsSchema = z.object({
  month: z.string(),
  totalCount: z.coerce.number(),
  matchCount: z.coerce.number(),
  matchedValueAmount: z.coerce.number(),
});

export type MonthlyDeflectionStats = z.infer<
  typeof monthlyDeflectionStatsSchema
>;

/**
 * One row of the deflection funnel, at whatever grain the caller grouped by
 * (`key` is 'all' for the ungrouped funnel).
 *
 * `settled` and `alerted` partition `receipts` exactly: a lookup we could not
 * resolve to an order produced no receipt, and with no order it cannot join to
 * an alert either, so every alerted row is necessarily a receipt.
 */
export const deflectionSegmentRowSchema = z.object({
  key: z.string(),
  lookupCount: z.coerce.number(),
  receiptCount: z.coerce.number(),
  settledCount: z.coerce.number(),
  alertedCount: z.coerce.number(),
  chargebackCount: z.coerce.number(),
});

export type DeflectionSegmentRow = z.infer<typeof deflectionSegmentRowSchema>;

export const resolveStatsSchema = z.object({
  totalCount: z.coerce.number(),
  matchCount: z.coerce.number(),
  noMatchCount: z.coerce.number(),
  multipleCount: z.coerce.number(),
  timeoutCount: z.coerce.number(),
  schemaErrorCount: z.coerce.number(),
  partnerErrorCount: z.coerce.number(),
  errorCount: z.coerce.number(),
  respondedCount: z.coerce.number(),
  slaMetCount: z.coerce.number(),
  matchedValueAmount: z.coerce.number(),
  p50LatencyMs: z.coerce.number().nullable(),
  p95LatencyMs: z.coerce.number().nullable(),
  currency: z.string(),
  monthly: z.array(monthlyDeflectionStatsSchema).nullish(),
  /** Lookups the issuer's own call centre raised — the worst-converting source. */
  callLookupCount: z.coerce.number().default(0),
  /**
   * The ungrouped funnel, plus how many chargebacks the account had in range at
   * all. The chargeback stage reads zero both when no lookup became one (good)
   * and when no lookup's order resolved to a chargeback (unknown); the count
   * beside it is what lets the UI tell those apart.
   */
  funnel: deflectionSegmentRowSchema
    .omit({ key: true })
    .extend({ chargebacksInRange: z.coerce.number() })
    .nullish(),
  byChannel: z.array(deflectionSegmentRowSchema).nullish(),
  byIssuer: z.array(deflectionSegmentRowSchema).nullish(),
  byNetwork: z.array(deflectionSegmentRowSchema).nullish(),
  errors: z.array(z.string()).nullish(),
  priorPeriod: z
    .object({
      totalCount: z.coerce.number(),
      matchCount: z.coerce.number(),
      respondedCount: z.coerce.number(),
      slaMetCount: z.coerce.number(),
      matchedValueAmount: z.coerce.number(),
    })
    .nullish(),
});

export type ResolveStats = z.infer<typeof resolveStatsSchema>;
